import "./ChatPage.scss";
import type React from "react";
import {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {Box} from "@mui/material";
import {Sidebar} from "./components/Sidebar";
import {useNavigate, useParams} from "react-router";
import {useChatContext} from "./context/ChatContext";
import {LoadingBarComponent} from "../../components/loading-bar/LoadingBarComponent.tsx";
import {ChatArea} from "./components/ChatArea/ChatArea";
import {useChatAvailableForActiveDatabag} from "./hooks/useChatAvailableForActiveDatabag";
import {useStores} from "../../stores";
import {tryGeneratePath} from "../../routing/routing.utils";

export const ChatPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams();
    const {bagStore} = useStores();
    const {clearSessionLoading} = useChatContext();
    const {isReady, isAvailable} = useChatAvailableForActiveDatabag();

    useEffect(() => {
        if (!isReady || isAvailable) {
            return;
        }
        const path = tryGeneratePath(
            params,
            ["homepage_dashboard", "home"],
            bagStore.bagId,
        );
        if (path) {
            navigate(path, {replace: true});
        }
    }, [isReady, isAvailable, navigate, params, bagStore.bagId]);

    // ChatProvider outlives this page — clear restore/switch loading on unmount.
    useEffect(() => {
        return () => {
            clearSessionLoading();
        };
    }, [clearSessionLoading]);

    if (isReady && !isAvailable) {
        return <LoadingBarComponent/>;
    }

    // Uses a custom version of the <MainPageFacade/> to better suit the chat page layout
    return (
        <>
            <LoadingBarComponent/>
            <main id="main-component-content">
                <div id="main-page-facade" className="chat-page">
                    <Box
                        className="chat-page-content"
                        sx={{
                            display: "flex",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            overflow: "hidden",
                        }}
                    >
                        <Sidebar/>
                        <ChatArea/>
                    </Box>
                </div>
            </main>
        </>
    );
});
